import { textoDaObra } from "@/shared/utils/formatar-identificacao-obra";
import { useEffect, useMemo, useRef, useState } from "react";

import { todayIso } from "@/modules/carga-trabalho";
import {
  EQUIPE_OBRA_FORBIDDEN_MESSAGE,
  formatDataBr,
  useDefinirEquipeObra,
  useEquipeObra,
  useEquipeObraOpcoes,
} from "@/modules/recursos";
import type { DivergenciasAlocacao } from "@/modules/recursos";
import {
  alteracoesDaAcao,
  aplicarDisponibilidadeSimulada,
  ausenciasDoProfissional,
  isSemEfeito,
  membrosComEfeito,
  SIMULACAO_APLICAR_LABEL,
  SIMULACAO_NADA_ALTERADO,
} from "@/modules/recursos/simulacao";
import type { AlteracaoEquipe } from "@/modules/recursos/simulacao";
import type { EquipeRow } from "@/shared/components/equipe-obra/equipe-obra.types";
import {
  EQUIPE_DIALOG_TITLE,
  EQUIPE_EDIT_DIALOG_TITLE,
  EQUIPE_GRAVADA_EMPTY_MESSAGE,
  EQUIPE_TABS,
} from "./definir-equipe-obra.constants";
import { EQUIPE_EMPTY_MESSAGE } from "@/shared/components/equipe-obra/equipe-obra.constants";
import {
  hasErrors,
  isRowAlterada,
  membroDaLinha,
  mensagemSucesso,
  novasAlocacoes,
} from "./definir-equipe-obra.utils";
import { useEquipeGravada } from "./hooks/use-equipe-gravada";
import { useEquipeLinhas } from "./hooks/use-equipe-linhas";
import { useSalvarComVerificacoes } from "./hooks/use-salvar-com-verificacoes";
import type { SituacoesConfirmadas } from "./hooks/use-salvar-com-verificacoes";
import type { DefinirEquipeObraDialogProps, TabId } from "./types-definir-equipe-obra";
import { divergenciasParaVerificacao, resumirPapeisObrigatorios } from "@/modules/recursos";

/**
 * Estado e ações do modal "Definir Equipe de Obra": as linhas (criação ou equipe gravada), o Salvar com as
 * verificações (conflitos → indisponibilidade → equipe incompleta) e, no Modo Simulação, a aplicação no cenário.
 */
export function useDefinirEquipeObraDialog({
  obra,
  onHide,
  onNotify,
  onSaved,
  equipeDefinida = false,
  simulacao,
}: DefinirEquipeObraDialogProps) {
  const opcoes = useEquipeObraOpcoes(obra.inicio, obra.fim);
  // RN14: quem já está numa equipe simulada no período aparece como indisponível.
  const cenario = simulacao?.cenario;
  const profissionais = useMemo(
    () =>
      cenario
        ? aplicarDisponibilidadeSimulada(opcoes.profissionais, cenario, obra)
        : opcoes.profissionais,
    [opcoes.profissionais, cenario, obra],
  );
  const { definir, isSubmitting } = useDefinirEquipeObra();

  const [tab, setTab] = useState<TabId>("profissionais");
  const [obrigatoriosAbertos, setObrigatoriosAbertos] = useState(false);
  const overlaySimulacao = useMemo(() => {
    if (!simulacao) return undefined;
    // Só a alteração do cenário: o `anterior` da carga não traz uma alocação por linha e
    // as linhas intactas do mesmo profissional passariam a valer como alteradas.
    return alteracoesDaAcao(simulacao.cenario, "definir-equipe").find(
      (item) => item.obra.id === simulacao.obra.id,
    )?.novo;
  }, [simulacao]);

  const today = useMemo(() => todayIso(), []);

  // Falhas de carga: Toast uma vez por falha (papéis e profissionais são independentes).
  const notified = useRef({ papeis: false, profissionais: false, forbidden: false });
  useEffect(() => {
    if (opcoes.status === "loading") return;

    if (opcoes.forbidden && !notified.current.forbidden) {
      notified.current.forbidden = true;
      onNotify({ severity: "error", summary: EQUIPE_OBRA_FORBIDDEN_MESSAGE });
      return;
    }
    if (opcoes.profissionaisError && !notified.current.profissionais && !opcoes.forbidden) {
      notified.current.profissionais = true;
      onNotify({ severity: "error", summary: opcoes.profissionaisError });
    }
    if (opcoes.papeisError && !notified.current.papeis && !opcoes.forbidden) {
      notified.current.papeis = true;
      onNotify({ severity: "error", summary: opcoes.papeisError });
    }
  }, [opcoes, onNotify]);

  const papeisById = useMemo(
    () => new Map(opcoes.papeis.map((papel) => [papel.id, papel])),
    [opcoes.papeis],
  );
  const obrigatorios = useMemo(
    () => opcoes.papeis.filter((papel) => papel.obrigatorio),
    [opcoes.papeis],
  );

  const equipe = useEquipeObra(obra.id, { enabled: equipeDefinida });
  const periodoObra = {
    inicio: (equipeDefinida && equipe.obra?.inicio) || obra.inicio,
    fim: (equipeDefinida && equipe.obra?.fim) || obra.fim,
  };

  const linhas = useEquipeLinhas({ obra, periodoObra, papeisById, onNotify });
  const rows = linhas.linhas;

  const gravada = useEquipeGravada({
    obra,
    enabled: equipeDefinida,
    equipe,
    carregarLinhas: linhas.carregarLinhas,
    registrarGravada: linhas.registrarGravada,
    reabrirComErro: linhas.reabrirComErro,
    marcarFalha: linhas.marcarFalha,
    avisar: linhas.avisar,
    onNotify,
    onSaved,
    onHide,
    onDivergencias: reabrirComDivergenciasDoServidor,
    overlaySimulacao,
  });
  const busy = isSubmitting || gravada.saving;

  const novas = novasAlocacoes(rows, papeisById);
  // Equipe gravada: só as linhas com período alterado entram na verificação (as demais já
  // estavam gravadas e não são reavaliadas).
  const novasAVerificar = equipeDefinida
    ? novas.filter((nova) => rows.some((row) => row.id === nova.id && isRowAlterada(row)))
    : novas;

  // Conflitos → indisponibilidade → equipe incompleta, e só então o POST (ou os PATCH).
  const verificacoes = useSalvarComVerificacoes({
    obra,
    validar: () => !isSubmitting && !gravada.saving && linhas.validarTudo(),
    novas: novasAVerificar,
    // Na equipe gravada não se adicionam papéis: não há o que avisar como incompleto.
    resumoObrigatorios: equipeDefinida
      ? []
      : resumirPapeisObrigatorios(obrigatorios, rows, profissionais, obra),
    listagem: profissionais,
    // O Toast da tela fica atrás do modal: o erro também aparece dentro dele.
    onErroVerificacao: (mensagem) => {
      linhas.avisar(mensagem);
      onNotify({ severity: "error", summary: mensagem });
    },
    acaoFinal: (justificativa, confirmadas) => {
      if (simulacao) return aplicarNaSimulacao();
      if (equipeDefinida) {
        return gravada.salvar(rows, {
          justificativa,
          summary: justificativa ? mensagemSucesso(justificativa, confirmadas) : undefined,
        });
      }
      return submit(justificativa, confirmadas);
    },
    somenteIncompleta: Boolean(simulacao),
  });

  async function aplicarNaSimulacao() {
    if (!simulacao) return;
    const contexto = { profissionais, papeis: papeisById, equipeDefinida };
    const anterior = equipeDefinida
      ? rows.map((row) =>
          membroDaLinha(row, row.base ?? { inicio: row.inicio, fim: row.fim }, contexto),
        )
      : simulacao.anterior;
    const novo = rows.map((row) =>
      membroDaLinha(row, { inicio: row.inicio, fim: row.fim }, contexto),
    );
    const alteracao: AlteracaoEquipe = {
      acao: "definir-equipe",
      obra: simulacao.obra,
      periodo: { inicio: obra.inicio, fim: obra.fim },
      anterior,
      novo,
    };

    if (isSemEfeito(alteracao)) {
      onNotify({ severity: "error", summary: SIMULACAO_NADA_ALTERADO });
      linhas.avisar(SIMULACAO_NADA_ALTERADO);
      return;
    }

    // As ausências vêm da consulta, sem as ocupações simuladas (essas já são conflitos da carga).
    const efeito = membrosComEfeito(alteracao.anterior, alteracao.novo);
    const ausencias = efeito.flatMap((membro) => {
      const profissional = opcoes.profissionais.find((item) => item.id === membro.usuarioId);
      return profissional
        ? ausenciasDoProfissional(profissional, { obra: simulacao.obra, ...membro })
        : [];
    });
    const resultado = await simulacao.onAplicar(alteracao, ausencias);
    // Sem mensagem, o usuário só voltou da justificativa dos conflitos novos.
    if (!resultado.ok && resultado.mensagem) linhas.avisar(resultado.mensagem);
  }

  function confirmar() {
    if (simulacao && rows.length === 0) {
      onNotify({ severity: "error", summary: SIMULACAO_NADA_ALTERADO });
      linhas.avisar(SIMULACAO_NADA_ALTERADO);
      return;
    }
    if (simulacao && equipeDefinida) {
      if (!linhas.validarTudo()) return;
      void aplicarNaSimulacao();
      return;
    }
    void verificacoes.salvar();
  }

  /** Divergência do servidor (corrida): reabre os modais com os dados dele. */
  function reabrirComDivergenciasDoServidor(divergencias: DivergenciasAlocacao) {
    const { conflitos, indisponiveis } = divergenciasParaVerificacao(
      divergencias,
      novas,
      obra,
      (usuarioId) =>
        profissionais.find((item) => item.id === usuarioId)?.nome ||
        rows.find((row) => row.usuarioId === usuarioId)?.profissionalNome ||
        "",
      (papelObraId) =>
        papeisById.get(papelObraId)?.nome ??
        rows.find((row) => row.papelObraId === papelObraId)?.papelNome,
    );
    verificacoes.reabrirComDivergencias(conflitos, indisponiveis);
  }

  async function submit(justificativa?: string, confirmadas?: SituacoesConfirmadas) {
    const payload = rows.map((row) => ({
      papelObraId: row.papelObraId,
      usuarioId: row.usuarioId ?? "",
      inicio: row.inicio,
      fim: row.fim,
    }));

    const result = await definir(obra.id, payload, justificativa);

    if (result.ok) {
      onNotify({ severity: "success", summary: mensagemSucesso(justificativa, confirmadas) });
      onSaved();
      return;
    }

    if (result.divergencias) {
      reabrirComDivergenciasDoServidor(result.divergencias);
      return;
    }

    if (result.forbidden) {
      linhas.avisar(EQUIPE_OBRA_FORBIDDEN_MESSAGE);
      onNotify({ severity: "error", summary: EQUIPE_OBRA_FORBIDDEN_MESSAGE });
      return;
    }

    if (result.fieldErrors.size > 0) linhas.aplicarErrosDoServidor(result.fieldErrors);

    if (result.message) {
      linhas.avisar(result.message);
      if (result.fieldErrors.size === 0) {
        onNotify({ severity: "error", summary: result.message });
      }
    }
  }

  // Equipe gravada: Salvar só com alguma linha alterada e nenhuma em edição ou com erro.
  const salvarBloqueado =
    equipeDefinida &&
    (equipe.status !== "success" ||
      !rows.some(isRowAlterada) ||
      rows.some((row) => row.editing || hasErrors(row.errors)));
  const etapa = verificacoes.etapa;
  const estadoEquipe =
    equipeDefinida && equipe.status !== "success"
      ? (equipe.status as "forbidden" | "error" | "loading")
      : null;

  return {
    titulo: equipeDefinida ? EQUIPE_EDIT_DIALOG_TITLE : EQUIPE_DIALOG_TITLE,
    modoSimulacao: Boolean(simulacao),
    fechar: equipeDefinida ? gravada.fechar : onHide,
    aba: tab,
    abas: EQUIPE_TABS.map((item) => ({
      ...item,
      domId: `equipe-obra-tab-${item.id}`,
      ativa: item.id === tab,
    })),
    abaAtivaDomId: `equipe-obra-tab-${tab}`,
    selecionarAba: setTab,
    today,
    // O modal de equipe incompleta cita só o ID quando a obra não tem embarcação.
    obraRotulo: textoDaObra(obra),
    resumoObra: {
      nome: textoDaObra(obra),
      inicio: formatDataBr(periodoObra.inicio),
      fim: formatDataBr(periodoObra.fim),
    },
    obrigatorios,
    adicionados: new Set(rows.map((row) => row.papelObraId)),
    obrigatoriosAbertos,
    alternarObrigatorios: () => setObrigatoriosAbertos((aberto) => !aberto),
    carregandoOpcoes: opcoes.status === "loading",
    papeis: opcoes.papeis,
    papeisOpcoes: opcoes.papeis.map((papel) => ({ value: papel.id, label: papel.nome })),
    profissionais,
    profissionaisCalendario: opcoes.profissionais,
    profissionaisError: opcoes.profissionaisError,
    linhas: rows,
    novas,
    busy,
    formError: linhas.formError,
    estadoEquipe,
    equipeErro: equipe.status === "error" ? equipe.errorMessage : null,
    recarregarEquipe: equipe.status === "error" ? equipe.reload : undefined,
    vazioMensagem: equipeDefinida ? EQUIPE_GRAVADA_EMPTY_MESSAGE : EQUIPE_EMPTY_MESSAGE,
    adicionarPapel: linhas.adicionarLinha,
    alterarLinha: linhas.alterarLinha,
    confirmarLinha: linhas.confirmarLinha,
    cancelarLinha: linhas.cancelarLinha,
    editarLinha: linhas.editarLinha,
    removerLinha: linhas.removerLinha,
    salvarLabel: simulacao ? SIMULACAO_APLICAR_LABEL : "Salvar",
    salvarCarregando: busy || verificacoes.verificando,
    salvarBloqueado,
    confirmar,
    etapa,
    maxJustificativa: verificacoes.maxJustificativa,
    voltarVerificacao: verificacoes.cancelar,
    confirmarConflitos: verificacoes.confirmarConflitos,
    confirmarIndisponivel: verificacoes.confirmarIndisponivel,
    confirmarIncompleta: verificacoes.confirmarIncompleta,
  };
}
